import React, { Key, ReactNode, useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { ColumnHeightOutlined } from '@ant-design/icons'

import { LntButton, LntDrawer, LntIconButton } from '@app/components/base'
import { ExtendedTable } from '@app/components'
import { getColumnsFromMap } from '@app/utils/table'
import { Icon } from '@app/components/util'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { ICON_NAME } from '@app/constants/icon'
import { CATALOG_PRODUCTS_KEY } from '@app/constants/queryKeys'

import { getCatalogProductsDataFromResponse } from '@app/utils/responseAdapters'
import { objectHasNonNullableProp } from '@app/utils/object'

import { getLists } from '@app/api/routes/catalog'
import { postListsItemProducts } from '@app/api/routes/monitoringLists'

import { usePaginationParams, useToggle } from '@app/hooks'

import { ComplexModeModal } from '@app/pages/RegistryPageInner/ui'

import { addMonitoringListsProducts } from '@app/api/routes/monitoringListsProducts'

import { AddProductsForm } from './AddProductsForm'

import styles from './styles.module.scss'

import type { AddProductsDrawerTypes, ProductsDrawerColumns, AddProductFiltersData } from './types'
import type { CatalogProductsDataSource } from '@app/types/dataSource'
import type { Column } from '@app/components/base/Table/types'

const toolbar: ReactNode[] = [
  <LntIconButton
    key='column-height-button'
    type={ BUTTON_TYPE.text }
    size={ SIZE.small }
    icon={ <ColumnHeightOutlined /> }
  />,

  <LntIconButton
    key='search-button'
    type={ BUTTON_TYPE.text }
    size={ SIZE.small }
    icon={ <Icon name={ ICON_NAME.search } /> }
  />
]

const columnTitleMap: Record<keyof ProductsDrawerColumns, string> = {
  code: 'Код товара',
  name: 'Товар',
  trend: 'Направление',
  trendId: 'Код направления',
  purchasingGroup: 'ГЗ',
  purchasingGroupId: 'Код ГЗ',
  commodityGroup: 'Тов. Группа',
  commodityGroupId: 'Код тов. группы',
  category: 'Категория',
  categoryId: 'Код категории',
  subcategory: 'Подкатегория',
  subcategoryId: 'Код подкатегории'
}

const columns: Array<Column<ProductsDrawerColumns>> = getColumnsFromMap<ProductsDrawerColumns>({
  columnTitleMap,
  options: [
    ...Object.keys(columnTitleMap).map(key => ({
      key,
      sorter: true
    }))
  ]
})

export const AddProductsDrawer: React.FC<AddProductsDrawerTypes> = ({
  id,
  isOpen,
  isComplexMode,
  onClose,
  onProductAdded
}) => {
  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationParams()

  const [dataFilters, setDataFilters] = useState<AddProductFiltersData>({})
  const [selectedProducts, setSelectedProducts] = useState<string[]>([])

  const complexModeModalToggle = useToggle()

  const hasFiltersData = useMemo(() => objectHasNonNullableProp(dataFilters), [dataFilters])
  const dataListParams = useMemo(() => ({ ...paginationParams, ...dataFilters }), [paginationParams, dataFilters])

  const {
    data: dataList,
    refetch: dataListRefetch,
    remove: dataListRemove,
    isLoading: isDataListLoading
  } = useQuery({
    enabled: hasFiltersData,
    queryKey: [CATALOG_PRODUCTS_KEY.productsList, dataListParams],
    queryFn: async () => await getLists(dataListParams)
  })

  const postListsMutation = useMutation(postListsItemProducts)
  const addMonitoringListsProductsMutation = useMutation(addMonitoringListsProducts)

  const dataSource = useMemo(() => getCatalogProductsDataFromResponse(dataList), [dataList])

  const handleSubmitForm = (data: AddProductFiltersData): void => {
    setDataFilters(data)
    resetCurrentPage()
  }

  const handlePaginationChange = (current: number, pageSize: number): void => {
    setPagination({ current, pageSize })
  }

  const handleSelectedRows = (keys: Key[]): void => {
    const stringsArray = keys.map(key => String(key))
    setSelectedProducts(stringsArray)
  }

  const handleClose = (): void => {
    onClose?.()
    onProductAdded?.()
    setDataFilters({})
    dataListRemove()
  }

  const handlePostLists = (): void => {
    if (isComplexMode) {
      complexModeModalToggle.setToggled()

      return
    }

    if (!selectedProducts.length) return

    const data = {
      productIds: selectedProducts
    }
    postListsMutation.mutate({ id, data }, { onSettled: handleClose })
  }

  const handleComplexModeAddProducts = (monitoringListIds: number[]): void => {
    if (!selectedProducts.length) return

    addMonitoringListsProductsMutation.mutate(
      { productIds: selectedProducts, monitoringListIds: [...monitoringListIds, id] },
      { onSuccess: handleClose, onSettled: complexModeModalToggle.setUntoggled }
    )
  }

  useEffect(() => {
    if (hasFiltersData) {
      dataListRefetch()
    }
  }, [hasFiltersData])

  return (
    <>
      <LntDrawer
        open={ isOpen }
        title='Добавление товаров из справочника'
        width={ 960 }
        onClose={ handleClose }
      >
        <div className={ styles.addProductsDrawer }>
          <AddProductsForm onSubmit={ handleSubmitForm } />

          <ExtendedTable<CatalogProductsDataSource>
            loading={ hasFiltersData && isDataListLoading }
            columns={ columns as Array<Column<CatalogProductsDataSource>> }
            dataSource={ dataSource }
            toolbar={ toolbar }
            rowSelection={ {
              onChange: handleSelectedRows
            } }
            pagination={ {
              ...pagination,
              disabled: !hasFiltersData,
              total: dataList?.count,
              onChange: handlePaginationChange
            } }
          />

          <LntButton
            className={ styles.addProductsDrawer__uploadButton }
            type={ BUTTON_TYPE.primary }
            size={ SIZE.middle }
            onClick={ handlePostLists }
            disabled={ !selectedProducts.length }
          >
            Загрузить
          </LntButton>
        </div>
      </LntDrawer>

      <ComplexModeModal
        title='Добавление товаров в'
        submitText='Добавить'
        open={ complexModeModalToggle.value }
        onSubmit={ handleComplexModeAddProducts }
        onCancel={ complexModeModalToggle.setUntoggled }
      />
    </>
  )
}
